import React, { useEffect, useMemo, useState } from "react";

const SITE_URL = "https://datanex.ai/datanexai-app";
const ASSET_BASE = "/wp-content/reactpress/apps/datanexai";
const asset = (name) => `${ASSET_BASE}/images/${encodeURIComponent(name)}`;
const WHATSAPP_LINK = "https://wa.me/5511000000000";

const BRAND = {
  bg: "#f4f7fb",
  bg2: "#eef4fb",
  surface: "rgba(255,255,255,0.80)",
  surfaceStrong: "#ffffff",
  line: "rgba(15,23,42,0.08)",
  text: "#0f172a",
  muted: "#475569",
  purple: "#7c3aed",
  magenta: "#d946ef",
  cyan: "#06b6d4",
  teal: "#14b8a6",
  blue: "#2563eb",
  orange: "#f97316",
  gold: "#f59e0b",
  green: "#10b981",
  shadow: "0 24px 80px rgba(15,23,42,0.10)",
  shadowSoft: "0 12px 40px rgba(15,23,42,0.08)",
};

const copy = {
  en: {
    language: "English",
    nav: { products: "Products", printed: "Printed System", outcomes: "Outcomes", compliance: "Compliance", contact: "Contact", demo: "Book Demo" },
    hero: {
      eyebrow: "School-first AI learning infrastructure",
      title: "The Operating System for Modern Learning.",
      subtitle:
        "DatanexAI connects insight, personalization, experimentation, and delivery into one elegant school-ready ecosystem.",
      body:
        "Most school technology solves a narrow task. DatanexAI brings together EduInsights, EduFlux, EduKitAI, EduBoxx, and a complete printed instructional system so schools can understand learners better, teach with more precision, and deliver stronger outcomes with less friction.",
      primary: "See how this works in your school",
      secondary: "Explore the ecosystem",
      tags: ["COPPA compliant", "FERPA compliant", "BNCC compatible", "English • Português • Español"],
    },
    problem: {
      title: "Schools are being asked to do the impossible with disconnected tools.",
      bullets: [
        "Understand how every student learns before they struggle.",
        "Personalize instruction without overloading teachers.",
        "Bring AI into learning in ways that are meaningful, not superficial.",
        "Deliver rich content reliably even where infrastructure is uneven.",
      ],
      kicker: "The problem",
    },
    model: {
      kicker: "The new model",
      title: "Insight → Personalization → Experimentation → Delivery",
      body: "Instead of buying isolated platforms that never talk to each other, schools can implement one coordinated learning system that moves from student understanding to classroom action to future-ready experiences to dependable access.",
    },
    productsIntro: {
      kicker: "The ecosystem",
      title: "Four pillars. One connected school intelligence architecture.",
      body: "Each solution stands on its own. Together, they form a school-wide operating layer for understanding, teaching, innovating, and delivering.",
    },
    products: {
      eduinsights: {
        name: "EduInsights",
        tagline: "Learning insight for transitions",
        problem: "Schools often make admissions, transition, and support decisions with fragmented academic data, subjective impressions, and little visibility into how students are likely to learn best.",
        current: "Most current solutions focus on admissions administration, testing, or diagnostic categories. They rarely produce practical learning insight educators can use immediately.",
        unique: "EduInsights creates a non-diagnostic insight experience that surfaces learning patterns, readiness signals, and support needs in a format schools and families can act on quickly.",
        benefits: "Schools gain better-fit enrollments, stronger transition planning, and earlier support decisions with less guesswork.",
        impact: "Students feel seen earlier. Parents receive clearer guidance. Teachers start with better context. Administrators reduce friction, mismatch, and avoidable escalation.",
      },
      eduflux: {
        name: "EduFlux",
        tagline: "Differentiate. Personalize. Deliver.",
        problem: "Teachers are expected to personalize instruction for very different learners while working within fixed curricula, limited time, and data that often arrives too late to change what happens in class.",
        current: "Traditional LMS and assessment tools organize content or measure performance, but rarely turn live learning signals into differentiated classroom action.",
        unique: "EduFlux turns lesson input, adaptive assessment, comprehension signals, and learning preferences into differentiated assignments and support pathways at classroom speed.",
        benefits: "Schools improve instructional precision, reduce teacher overload, and make personalization scalable instead of aspirational.",
        impact: "Students receive more relevant learning. Parents see more targeted support. Teachers save planning time. Administrators gain visibility into implementation quality and instructional effectiveness.",
      },
      edukitai: {
        name: "EduKitAI",
        tagline: "Explore. Experiment. Discover.",
        problem: "Schools want meaningful AI literacy, but many options are too theoretical, too technical, or too disconnected from real classroom practice and age-appropriate experimentation.",
        current: "Most AI education offerings emphasize isolated coding tools, robotics, or superficial demos without a structured model for guided experimentation and reflection.",
        unique: "EduKitAI combines hands-on AI experiences, guided experiments, and structured reflection so students can understand, test, and apply AI concepts in meaningful ways.",
        benefits: "Schools strengthen future-readiness, enrich project-based learning, and give teachers a practical path to integrate AI without needing deep technical expertise.",
        impact: "Students build curiosity and confidence. Parents see innovation with purpose. Teachers gain usable lab experiences. Administrators strengthen school differentiation and relevance.",
      },
      eduboxx: {
        name: "EduBoxx",
        tagline: "Reliable delivery where bandwidth is not",
        problem: "Many schools still struggle to deliver rich digital content consistently because internet capacity, infrastructure reliability, or classroom concurrency make access unstable.",
        current: "Most digital learning platforms assume always-on connectivity and break down in constrained environments or when multiple classrooms need simultaneous access.",
        unique: "EduBoxx uses local delivery architecture to distribute educational content reliably across classrooms, making rich media learning possible even when connectivity is limited.",
        benefits: "Schools reduce access bottlenecks, improve reliability, and protect learning continuity from network instability.",
        impact: "Students gain uninterrupted access. Parents see stronger continuity. Teachers plan with confidence. Administrators improve equity and technology ROI across classrooms.",
      },
    },
    printed: {
      kicker: "The complete learning system",
      title: "Not just software. A full instructional system schools can implement immediately.",
      body: "This is one of the strongest differentiators in the portfolio: a coordinated digital + human + physical implementation model.",
      manual: {
        title: "Teacher Manual",
        body: "Turns platform insight into classroom action with implementation guidance, instructional routines, and practical workflows teachers can use immediately.",
      },
      journals: {
        title: "Student Journals",
        body: "Build reflection, metacognition, ownership, and stronger learning transfer by making student thinking visible over time.",
      },
      strategies: {
        title: "Strategy Frameworks",
        body: "Provides structured learning strategies and instructional playbooks that help schools scale quality, consistency, and teacher confidence.",
      },
    },
    outcomes: {
      kicker: "Outcomes",
      title: "Clear value for every stakeholder.",
      students: "More relevant learning, stronger engagement, earlier support, and greater confidence.",
      parents: "More visibility, clearer communication, and more trust in how the school understands and supports their child.",
      teachers: "Less guesswork, stronger differentiation, better instructional guidance, and lower planning friction.",
      admins: "Better decisions, stronger retention, clearer implementation visibility, and improved school positioning.",
    },
    compliance: {
      kicker: "Compliance",
      title: "Built for responsible school deployment.",
      body: "DatanexAI solutions are designed for COPPA and FERPA compliant deployment models and are compatible with BNCC-aligned implementation strategies for Brazilian schools.",
    },
    contact: {
      kicker: "Conversion",
      title: "Turn interest into pilots, demos, and implementation conversations.",
      body: "Use the multilingual funnel to start a school conversation around admissions insight, instructional personalization, AI learning, infrastructure reliability, or the complete ecosystem.",
      cta: "Request a strategic demo",
      fields: {
        institution: "Institution name",
        name: "Full name",
        email: "Work email",
        phone: "Phone",
        role: "Role",
        country: "Country",
        interest: "Primary interest",
        message: "What challenge are you trying to solve?",
      },
      success: "Thanks — your inquiry has been captured.",
      roleOptions: ["School Leader", "Teacher", "Admissions", "Partner", "Investor", "Other"],
      interestOptions: ["EduInsights", "EduFlux", "EduKitAI", "EduBoxx", "Printed System", "Full Ecosystem"],
    },
  },
  pt: {
    language: "Português",
    nav: { products: "Produtos", printed: "Sistema Impresso", outcomes: "Impacto", compliance: "Conformidade", contact: "Contato", demo: "Agendar Demo" },
    hero: {
      eyebrow: "Infraestrutura educacional com IA, focada em escolas",
      title: "O Sistema Operacional da Aprendizagem Moderna.",
      subtitle:
        "A DatanexAI conecta insight, personalização, experimentação e entrega em um ecossistema elegante e pronto para a realidade escolar.",
      body:
        "A maior parte da tecnologia escolar resolve uma tarefa estreita. A DatanexAI reúne EduInsights, EduFlux, EduKitAI, EduBoxx e um sistema instrucional impresso completo para que escolas entendam melhor seus alunos, ensinem com mais precisão e entreguem resultados mais fortes com menos atrito.",
      primary: "Veja como isso funciona na sua escola",
      secondary: "Explorar o ecossistema",
      tags: ["Compatível com COPPA", "Compatível com FERPA", "Compatível com BNCC", "English • Português • Español"],
    },
    problem: {
      title: "As escolas estão sendo pressionadas a fazer o impossível com ferramentas desconectadas.",
      bullets: [
        "Entender como cada aluno aprende antes que ele tenha dificuldade.",
        "Personalizar a instrução sem sobrecarregar professores.",
        "Levar IA para a aprendizagem de forma significativa, não superficial.",
        "Entregar conteúdo rico com confiabilidade mesmo quando a infraestrutura é irregular.",
      ],
      kicker: "O problema",
    },
    model: {
      kicker: "O novo modelo",
      title: "Insight → Personalização → Experimentação → Entrega",
      body: "Em vez de comprar plataformas isoladas que nunca se conectam, as escolas podem implementar um sistema coordenado que vai da compreensão do aluno à ação em sala, à inovação prática e ao acesso confiável.",
    },
    productsIntro: {
      kicker: "O ecossistema",
      title: "Quatro pilares. Uma arquitetura conectada de inteligência escolar.",
      body: "Cada solução funciona sozinha. Juntas, elas formam uma camada operacional para entender, ensinar, inovar e entregar.",
    },
    products: {
      eduinsights: {
        name: "EduInsights",
        tagline: "Insight de aprendizagem para transições",
        problem: "As escolas frequentemente tomam decisões de admissão, transição e suporte com dados fragmentados, impressões subjetivas e pouca visibilidade sobre como os alunos provavelmente aprendem melhor.",
        current: "A maioria das soluções foca em administração, testes ou categorias diagnósticas. Raramente produzem insights práticos de aprendizagem que educadores possam usar imediatamente.",
        unique: "O EduInsights cria uma experiência de insight não diagnóstica que revela padrões de aprendizagem, sinais de prontidão e necessidades de suporte em um formato acionável para escolas e famílias.",
        benefits: "As escolas obtêm melhor aderência na matrícula, transições mais fortes e decisões de suporte mais precoces, com menos adivinhação.",
        impact: "Os estudantes são compreendidos mais cedo. Os pais recebem orientação mais clara. Os professores começam com melhor contexto. Os administradores reduzem atrito e desalinhamento.",
      },
      eduflux: {
        name: "EduFlux",
        tagline: "Diferencie. Personalize. Entregue.",
        problem: "Espera-se que os professores personalizem a instrução para aprendizes muito diferentes com currículo fixo, pouco tempo e dados que chegam tarde demais.",
        current: "LMS e avaliações tradicionais organizam conteúdo ou medem desempenho, mas raramente transformam sinais de aprendizagem em ação diferenciada na sala.",
        unique: "O EduFlux converte entrada de aula, avaliação adaptativa, compreensão e preferências de aprendizagem em tarefas diferenciadas e caminhos de suporte na velocidade da sala.",
        benefits: "As escolas melhoram a precisão instrucional, reduzem a sobrecarga docente e tornam a personalização escalável.",
        impact: "Os alunos recebem aprendizagem mais relevante. Os pais veem suporte mais direcionado. Os professores economizam tempo. Os administradores ganham visibilidade sobre a qualidade da implementação.",
      },
      edukitai: {
        name: "EduKitAI",
        tagline: "Explore. Experimente. Descubra.",
        problem: "As escolas querem letramento em IA com significado, mas muitas opções são teóricas demais, técnicas demais ou desconectadas da prática em sala.",
        current: "Grande parte das ofertas de IA na educação enfatiza ferramentas isoladas, robótica ou demonstrações superficiais sem um modelo estruturado de exploração guiada e reflexão.",
        unique: "O EduKitAI combina experiências práticas com IA, experimentos guiados e reflexão estruturada para que os alunos compreendam, testem e apliquem IA de forma significativa.",
        benefits: "As escolas fortalecem a preparação para o futuro, enriquecem projetos e dão aos professores um caminho prático para integrar IA à instrução.",
        impact: "Os alunos desenvolvem curiosidade e confiança. Os pais veem inovação com propósito. Os professores ganham experiências utilizáveis. Os administradores fortalecem a diferenciação da escola.",
      },
      eduboxx: {
        name: "EduBoxx",
        tagline: "Entrega confiável onde a banda não é",
        problem: "Muitas escolas ainda têm dificuldade para entregar conteúdo digital de forma consistente porque a conectividade, a infraestrutura e a concorrência entre salas tornam o acesso instável.",
        current: "A maioria das plataformas digitais assume conectividade constante e falha em ambientes limitados ou quando várias salas precisam de acesso simultâneo.",
        unique: "O EduBoxx usa arquitetura local para distribuir conteúdo educacional com confiabilidade entre salas, tornando o uso de mídia rica possível mesmo com conectividade limitada.",
        benefits: "As escolas reduzem gargalos de acesso, melhoram a confiabilidade e protegem a continuidade da aprendizagem contra instabilidade de rede.",
        impact: "Os estudantes têm acesso contínuo. Os pais veem mais consistência. Os professores planejam com confiança. Os administradores melhoram equidade e ROI tecnológico.",
      },
    },
    printed: {
      kicker: "O sistema completo de aprendizagem",
      title: "Não é apenas software. É um sistema instrucional completo, pronto para ser implementado.",
      body: "Este é um dos maiores diferenciais do portfólio: um modelo coordenado digital + humano + físico.",
      manual: {
        title: "Manual do Professor",
        body: "Transforma insight da plataforma em ação em sala por meio de orientação de implementação, rotinas instrucionais e fluxos práticos.",
      },
      journals: {
        title: "Diários do Estudante",
        body: "Fortalecem reflexão, metacognição, autoria e transferência da aprendizagem ao tornar o pensamento do aluno visível ao longo do tempo.",
      },
      strategies: {
        title: "Frameworks de Estratégias",
        body: "Fornecem estratégias de aprendizagem e playbooks instrucionais que ajudam as escolas a escalar qualidade, consistência e confiança docente.",
      },
    },
    outcomes: {
      kicker: "Impacto",
      title: "Valor claro para cada público.",
      students: "Aprendizagem mais relevante, maior engajamento, suporte mais cedo e mais confiança.",
      parents: "Mais visibilidade, comunicação mais clara e maior confiança em como a escola entende e apoia seu filho.",
      teachers: "Menos adivinhação, diferenciação mais forte, melhor orientação instrucional e menos atrito no planejamento.",
      admins: "Melhores decisões, maior retenção, visibilidade de implementação e melhor posicionamento institucional.",
    },
    compliance: {
      kicker: "Conformidade",
      title: "Construído para implantação responsável em escolas.",
      body: "As soluções DatanexAI são desenhadas para modelos compatíveis com COPPA e FERPA e são compatíveis com estratégias alinhadas à BNCC para escolas brasileiras.",
    },
    contact: {
      kicker: "Conversão",
      title: "Transforme interesse em pilotos, demos e conversas de implementação.",
      body: "Use o funil multilíngue para iniciar uma conversa escolar sobre insight de admissões, personalização instrucional, aprendizagem com IA, confiabilidade de infraestrutura ou o ecossistema completo.",
      cta: "Solicitar demo estratégica",
      fields: {
        institution: "Nome da instituição",
        name: "Nome completo",
        email: "E-mail corporativo",
        phone: "Telefone",
        role: "Função",
        country: "País",
        interest: "Interesse principal",
        message: "Que desafio você quer resolver?",
      },
      success: "Obrigado — seu contato foi registrado.",
      roleOptions: ["Líder Escolar", "Professor", "Admissões", "Parceiro", "Investidor", "Outro"],
      interestOptions: ["EduInsights", "EduFlux", "EduKitAI", "EduBoxx", "Sistema Impresso", "Ecossistema Completo"],
    },
  },
  es: {
    language: "Español",
    nav: { products: "Productos", printed: "Sistema Impreso", outcomes: "Impacto", compliance: "Cumplimiento", contact: "Contacto", demo: "Solicitar Demo" },
    hero: {
      eyebrow: "Infraestructura educativa con IA, centrada en escuelas",
      title: "El Sistema Operativo del Aprendizaje Moderno.",
      subtitle:
        "DatanexAI conecta insight, personalización, experimentación y entrega en un ecosistema elegante y listo para la realidad escolar.",
      body:
        "La mayoría de la tecnología escolar resuelve una tarea estrecha. DatanexAI reúne EduInsights, EduFlux, EduKitAI, EduBoxx y un sistema instruccional impreso completo para que las escuelas comprendan mejor a sus estudiantes, enseñen con más precisión y logren mejores resultados con menos fricción.",
      primary: "Vea cómo funciona en su escuela",
      secondary: "Explorar el ecosistema",
      tags: ["Compatible con COPPA", "Compatible con FERPA", "Compatible con BNCC", "English • Português • Español"],
    },
    problem: {
      title: "A las escuelas se les pide hacer lo imposible con herramientas desconectadas.",
      bullets: [
        "Entender cómo aprende cada estudiante antes de que tenga dificultades.",
        "Personalizar la instrucción sin sobrecargar a los docentes.",
        "Llevar IA al aprendizaje de forma significativa, no superficial.",
        "Entregar contenido rico de forma confiable incluso cuando la infraestructura es desigual.",
      ],
      kicker: "El problema",
    },
    model: {
      kicker: "El nuevo modelo",
      title: "Insight → Personalización → Experimentación → Entrega",
      body: "En lugar de comprar plataformas aisladas que nunca se conectan, las escuelas pueden implementar un sistema coordinado que va de la comprensión del estudiante a la acción en el aula, a la innovación práctica y al acceso confiable.",
    },
    productsIntro: {
      kicker: "El ecosistema",
      title: "Cuatro pilares. Una arquitectura conectada de inteligencia escolar.",
      body: "Cada solución funciona por separado. Juntas, forman una capa operativa para comprender, enseñar, innovar y entregar.",
    },
    products: {
      eduinsights: {
        name: "EduInsights",
        tagline: "Insight de aprendizaje para transiciones",
        problem: "Las escuelas suelen tomar decisiones de admisión, transición y apoyo con datos fragmentados, impresiones subjetivas y poca visibilidad sobre cómo aprenderán mejor los estudiantes.",
        current: "La mayoría de las soluciones actuales se enfocan en administración, pruebas o categorías diagnósticas. Rara vez entregan insight práctico de aprendizaje utilizable de inmediato.",
        unique: "EduInsights crea una experiencia de insight no diagnóstica que revela patrones de aprendizaje, señales de preparación y necesidades de apoyo en un formato accionable para escuelas y familias.",
        benefits: "Las escuelas obtienen mejor ajuste en matrícula, transiciones más sólidas y decisiones de apoyo más tempranas con menos intuición forzada.",
        impact: "Los estudiantes se sienten comprendidos antes. Los padres reciben orientación más clara. Los docentes comienzan con mejor contexto. Los administradores reducen fricción y desalineación.",
      },
      eduflux: {
        name: "EduFlux",
        tagline: "Diferencie. Personalice. Entregue.",
        problem: "Se espera que los docentes personalicen la instrucción para estudiantes muy distintos con currículos fijos, poco tiempo y datos que llegan demasiado tarde.",
        current: "Los LMS y evaluaciones tradicionales organizan contenido o miden desempeño, pero rara vez convierten señales de aprendizaje en acción diferenciada real en el aula.",
        unique: "EduFlux convierte entrada de la lección, evaluación adaptativa, comprensión y preferencias de aprendizaje en tareas diferenciadas y rutas de apoyo a velocidad de aula.",
        benefits: "Las escuelas mejoran precisión instruccional, reducen sobrecarga docente y vuelven escalable la personalización.",
        impact: "Los estudiantes reciben aprendizaje más relevante. Los padres ven apoyo más dirigido. Los docentes ahorran tiempo. Los administradores ganan visibilidad sobre la calidad de implementación.",
      },
      edukitai: {
        name: "EduKitAI",
        tagline: "Explore. Experimente. Descubra.",
        problem: "Las escuelas quieren alfabetización en IA con sentido, pero muchas opciones son demasiado teóricas, técnicas o desconectadas del aula.",
        current: "Gran parte de la oferta actual enfatiza herramientas aisladas, robótica o demostraciones superficiales sin un modelo estructurado de exploración guiada y reflexión.",
        unique: "EduKitAI combina experiencias prácticas con IA, experimentos guiados y reflexión estructurada para que los estudiantes comprendan, prueben y apliquen IA de forma significativa.",
        benefits: "Las escuelas fortalecen la preparación para el futuro, enriquecen proyectos y ofrecen a los docentes una vía práctica para integrar IA en la instrucción.",
        impact: "Los estudiantes desarrollan curiosidad y confianza. Los padres ven innovación con propósito. Los docentes obtienen experiencias utilizables. Los administradores fortalecen la diferenciación escolar.",
      },
      eduboxx: {
        name: "EduBoxx",
        tagline: "Entrega confiable donde el ancho de banda no lo es",
        problem: "Muchas escuelas aún tienen dificultades para entregar contenido digital de forma consistente porque la conectividad, la infraestructura y la concurrencia entre aulas hacen el acceso inestable.",
        current: "La mayoría de las plataformas digitales asume conectividad constante y falla en entornos limitados o cuando varias aulas necesitan acceso simultáneo.",
        unique: "EduBoxx usa arquitectura local para distribuir contenido educativo con confiabilidad entre aulas, haciendo posible el aprendizaje con medios ricos incluso con conectividad limitada.",
        benefits: "Las escuelas reducen cuellos de botella, mejoran confiabilidad y protegen la continuidad del aprendizaje frente a la inestabilidad de red.",
        impact: "Los estudiantes tienen acceso continuo. Los padres ven más continuidad. Los docentes planifican con confianza. Los administradores mejoran equidad y ROI tecnológico.",
      },
    },
    printed: {
      kicker: "El sistema completo de aprendizaje",
      title: "No es solo software. Es un sistema instruccional completo que las escuelas pueden implementar de inmediato.",
      body: "Este es uno de los diferenciadores más fuertes del portafolio: un modelo coordinado digital + humano + físico.",
      manual: {
        title: "Manual del Docente",
        body: "Convierte el insight de la plataforma en acción de aula mediante guías de implementación, rutinas instruccionales y flujos prácticos.",
      },
      journals: {
        title: "Jornales del Estudiante",
        body: "Fortalecen reflexión, metacognición, apropiación y transferencia del aprendizaje al hacer visible el pensamiento del estudiante en el tiempo.",
      },
      strategies: {
        title: "Frameworks de Estrategias",
        body: "Aportan estrategias de aprendizaje y playbooks instruccionales que ayudan a escalar calidad, consistencia y confianza docente.",
      },
    },
    outcomes: {
      kicker: "Impacto",
      title: "Valor claro para cada actor.",
      students: "Aprendizaje más relevante, mayor engagement, apoyo más temprano y más confianza.",
      parents: "Más visibilidad, comunicación más clara y mayor confianza en cómo la escuela comprende y apoya a su hijo.",
      teachers: "Menos intuición forzada, mejor diferenciación, mejor guía instruccional y menos fricción de planificación.",
      admins: "Mejores decisiones, mayor retención, visibilidad de implementación y mejor posicionamiento institucional.",
    },
    compliance: {
      kicker: "Cumplimiento",
      title: "Construido para una implementación escolar responsable.",
      body: "Las soluciones DatanexAI están diseñadas para modelos compatibles con COPPA y FERPA y son compatibles con estrategias alineadas con BNCC para escuelas brasileñas.",
    },
    contact: {
      kicker: "Conversión",
      title: "Convierta interés en pilotos, demos y conversaciones de implementación.",
      body: "Use el embudo multilingüe para iniciar una conversación escolar sobre insight de admisiones, personalización instruccional, aprendizaje con IA, confiabilidad de infraestructura o el ecosistema completo.",
      cta: "Solicitar demo estratégica",
      fields: {
        institution: "Nombre de la institución",
        name: "Nombre completo",
        email: "Correo laboral",
        phone: "Teléfono",
        role: "Rol",
        country: "País",
        interest: "Interés principal",
        message: "¿Qué desafío quiere resolver?",
      },
      success: "Gracias — su contacto ha sido registrado.",
      roleOptions: ["Líder Escolar", "Docente", "Admisiones", "Socio", "Inversionista", "Otro"],
      interestOptions: ["EduInsights", "EduFlux", "EduKitAI", "EduBoxx", "Sistema Impreso", "Ecosistema Completo"],
    },
  },
};

const PRODUCTS = ["eduinsights", "eduflux", "edukitai", "eduboxx"];
const PRODUCT_META = {
  eduinsights: { a: BRAND.teal, b: BRAND.cyan },
  eduflux: { a: BRAND.blue, b: BRAND.cyan },
  edukitai: { a: BRAND.purple, b: BRAND.magenta },
  eduboxx: { a: BRAND.orange, b: BRAND.gold },
};

function useMotionStyles() {
  useEffect(() => {
    const styleId = "datanexai-super-elite-styles";
    if (document.getElementById(styleId)) return;
    const style = document.createElement("style");
    style.id = styleId;
    style.innerHTML = `
      html { scroll-behavior: smooth; }
      * { box-sizing: border-box; }
      body { margin: 0; }
      @keyframes floatSlow { 0% { transform: translateY(0px) translateX(0px);} 50% { transform: translateY(-12px) translateX(8px);} 100% { transform: translateY(0px) translateX(0px);} }
      @keyframes pulseGlow { 0% { opacity: .14; transform: scale(1);} 50% { opacity: .24; transform: scale(1.06);} 100% { opacity: .14; transform: scale(1);} }
      @keyframes drift { 0% { transform: translateX(0px);} 50% { transform: translateX(8px);} 100% { transform: translateX(0px);} }
      .se-float { animation: floatSlow 10s ease-in-out infinite; }
      .se-pulse { animation: pulseGlow 8s ease-in-out infinite; }
      .se-drift { animation: drift 7s ease-in-out infinite; }
      .se-hover-up { transition: transform .35s ease, box-shadow .35s ease, border-color .35s ease; }
      .se-hover-up:hover { transform: translateY(-6px); box-shadow: 0 28px 90px rgba(15,23,42,.12); border-color: rgba(15,23,42,.14); }
      .se-link { text-decoration: none; }
    `;
    document.head.appendChild(style);
  }, []);
}

function Glass({ children, className = "", style = {} }) {
  return (
    <div
      className={className}
      style={{
        background: BRAND.surface,
        border: `1px solid ${BRAND.line}`,
        boxShadow: BRAND.shadowSoft,
        backdropFilter: "blur(18px)",
        WebkitBackdropFilter: "blur(18px)",
        borderRadius: 28,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Section({ id, children, style = {} }) {
  return (
    <section id={id} style={{ maxWidth: 1240, margin: "0 auto", padding: "88px 20px", position: "relative", ...style }}>
      {children}
    </section>
  );
}

function Glow({ left, right, top, bottom, size = 240, from = BRAND.purple, to = BRAND.cyan, className = "" }) {
  return (
    <div
      className={className}
      aria-hidden
      style={{
        position: "absolute",
        left,
        right,
        top,
        bottom,
        width: size,
        height: size,
        borderRadius: 999,
        filter: "blur(60px)",
        opacity: 0.18,
        background: `radial-gradient(circle at 30% 30%, ${from}, ${to})`,
        pointerEvents: "none",
      }}
    />
  );
}

function GridOverlay() {
  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        opacity: 0.12,
        backgroundImage:
          "linear-gradient(rgba(15,23,42,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.08) 1px, transparent 1px)",
        backgroundSize: "44px 44px",
        maskImage: "radial-gradient(circle at center, black 35%, transparent 85%)",
        pointerEvents: "none",
      }}
    />
  );
}

function DatanexLogo() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
      <img src={asset("Logo_Datanex-v2.png")} alt="DatanexAI" style={{ height: 46, width: "auto", objectFit: "contain" }} />
      <div style={{ fontSize: 22, fontWeight: 700, color: BRAND.text, letterSpacing: 0.2 }}>DatanexAI</div>
    </div>
  );
}

function ProductLogoMark({ product, size = 72 }) {
  const meta = PRODUCT_META[product];
  return (
    <svg viewBox="0 0 120 120" width={size} height={size} role="img" aria-label={product}>
      <defs>
        <linearGradient id={`${product}-g`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={meta.a} />
          <stop offset="100%" stopColor={meta.b} />
        </linearGradient>
      </defs>
      <circle cx="60" cy="60" r="46" fill="none" stroke={`url(#${product}-g)`} strokeWidth="10" opacity="0.18" />
      <path d="M18 76 C34 40, 52 34, 70 56 S93 77, 102 30" fill="none" stroke={`url(#${product}-g)`} strokeWidth="11" strokeLinecap="round" />
      <path d="M27 92 C49 60, 69 57, 86 74" fill="none" stroke={`url(#${product}-g)`} strokeWidth="7" strokeLinecap="round" opacity="0.55" />
      <circle cx="18" cy="76" r="8" fill={meta.a} />
      <circle cx="52" cy="34" r="7" fill={meta.b} />
      <circle cx="102" cy="30" r="8" fill={meta.a} />
      <circle cx="86" cy="74" r="7" fill={meta.b} />
    </svg>
  );
}

function HeroVisual() {
  return (
    <Glass className="se-hover-up" style={{ position: "relative", overflow: "hidden", minHeight: 470, padding: 36 }}>
      <Glow className="se-pulse" left={-30} top={-10} size={200} from={BRAND.purple} to={BRAND.magenta} />
      <Glow className="se-pulse" right={-10} bottom={-10} size={240} from={BRAND.cyan} to={BRAND.teal} />
      <GridOverlay />
      <div style={{ position: "relative", zIndex: 2, height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
        <div style={{ display: "inline-flex", alignSelf: "flex-start", borderRadius: 999, padding: "10px 14px", border: `1px solid ${BRAND.line}`, background: "rgba(255,255,255,0.74)", color: BRAND.muted, fontSize: 12, letterSpacing: 2, textTransform: "uppercase" }}>
          Super Elite AI visual
        </div>
        <svg viewBox="0 0 740 440" style={{ width: "100%" }} className="se-float">
          <defs>
            <linearGradient id="hero-flow" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={BRAND.purple} />
              <stop offset="45%" stopColor={BRAND.cyan} />
              <stop offset="100%" stopColor={BRAND.orange} />
            </linearGradient>
          </defs>
          <path d="M52 296 C166 92, 280 94, 368 188 S548 374, 678 134" fill="none" stroke="url(#hero-flow)" strokeWidth="22" strokeLinecap="round" />
          <path d="M68 340 C176 134, 292 144, 382 230 S540 384, 654 190" fill="none" stroke="url(#hero-flow)" strokeWidth="8" strokeLinecap="round" opacity="0.45" />
          <circle cx="52" cy="296" r="14" fill={BRAND.purple} />
          <circle cx="250" cy="120" r="12" fill={BRAND.cyan} />
          <circle cx="368" cy="188" r="14" fill={BRAND.orange} />
          <circle cx="678" cy="134" r="16" fill={BRAND.magenta} />
          <circle cx="562" cy="310" r="10" fill={BRAND.teal} />
          <rect x="454" y="50" rx="22" ry="22" width="190" height="84" fill="rgba(255,255,255,0.82)" stroke="rgba(15,23,42,0.08)" />
          <rect x="80" y="82" rx="22" ry="22" width="154" height="76" fill="rgba(255,255,255,0.80)" stroke="rgba(15,23,42,0.08)" />
          <rect x="262" y="304" rx="22" ry="22" width="196" height="86" fill="rgba(255,255,255,0.84)" stroke="rgba(15,23,42,0.08)" />
        </svg>
        <div style={{ fontSize: 18, color: BRAND.muted, fontWeight: 500 }}>Insight, personalization, experimentation, and reliable delivery</div>
      </div>
    </Glass>
  );
}

function EcosystemDiagram() {
  return (
    <Glass style={{ position: "relative", overflow: "hidden", padding: 28 }} className="se-hover-up">
      <GridOverlay />
      <div style={{ position: "relative", zIndex: 2 }}>
        <div style={{ fontSize: 12, letterSpacing: 2, textTransform: "uppercase", color: BRAND.muted }}>Connected ecosystem</div>
        <div style={{ marginTop: 18, display: "grid", gap: 18 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16, flexWrap: "wrap" }}>
            <Node product="eduinsights" label="EduInsights" />
            <ArrowFlow />
            <Node product="eduflux" label="EduFlux" />
            <ArrowFlow />
            <Node product="edukitai" label="EduKitAI" />
            <ArrowFlow />
            <Node product="eduboxx" label="EduBoxx" />
          </div>
        </div>
      </div>
    </Glass>
  );
}

function ArrowFlow() {
  return (
    <div className="se-drift" style={{ display: "flex", alignItems: "center", justifyContent: "center", color: BRAND.muted, fontSize: 20, fontWeight: 700 }}>
      →
    </div>
  );
}

function Node({ product, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderRadius: 20, background: "rgba(255,255,255,0.78)", border: `1px solid ${BRAND.line}`, boxShadow: BRAND.shadowSoft }}>
      <ProductLogoMark product={product} size={44} />
      <div style={{ fontWeight: 600, color: BRAND.text }}>{label}</div>
    </div>
  );
}

function ProductFeatureCard({ productKey, content, labels }) {
  return (
    <Glass className="se-hover-up" style={{ overflow: "hidden" }}>
      <div style={{ display: "grid", gridTemplateColumns: "220px 1fr" }}>
        <div style={{ background: `linear-gradient(135deg, ${PRODUCT_META[productKey].a}, ${PRODUCT_META[productKey].b})`, minHeight: 100, padding: 28, position: "relative" }}>
          <div style={{ position: "absolute", inset: 0, background: "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.35), transparent 30%)" }} />
          <div style={{ position: "relative", zIndex: 2 }}>
            <ProductLogoMark product={productKey} size={86} />
          </div>
        </div>
        <div style={{ padding: 28 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div>
              <div style={{ fontSize: 28, fontWeight: 700, color: BRAND.text }}>{content.name}</div>
              <div style={{ marginTop: 4, color: BRAND.muted }}>{content.tagline}</div>
            </div>
          </div>
          <div style={{ marginTop: 18 }}>
            <SmallBlock title={labels.problem} body={content.problem} />
            <SmallBlock title={labels.unique} body={content.unique} compact />
          </div>
        </div>
      </div>
    </Glass>
  );
}

function SmallBlock({ title, body, compact = false }) {
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 2, color: BRAND.muted }}>{title}</div>
      <p style={{ marginTop: 8, color: BRAND.text, lineHeight: compact ? 1.7 : 1.8, fontSize: compact ? 16 : 17 }}>{body}</p>
    </div>
  );
}

function ProductDeepSection({ productKey, content, labels }) {
  return (
    <Section>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 0.92fr", gap: 32, alignItems: "start" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <ProductLogoMark product={productKey} size={72} />
            <div>
              <div style={{ fontSize: 34, fontWeight: 700, color: BRAND.text }}>{content.name}</div>
              <div style={{ color: BRAND.muted, fontSize: 18 }}>{content.tagline}</div>
            </div>
          </div>
          <div style={{ marginTop: 22, display: "grid", gap: 16 }}>
            <InfoPanel title={labels.problem} body={content.problem} />
            <InfoPanel title={labels.current} body={content.current} />
            <InfoPanel title={labels.unique} body={content.unique} />
            <InfoPanel title={labels.benefits} body={content.benefits} />
            <InfoPanel title={labels.impact} body={content.impact} />
          </div>
        </div>
        <ProductIllustration productKey={productKey} />
      </div>
    </Section>
  );
}

function InfoPanel({ title, body }) {
  return (
    <Glass className="se-hover-up" style={{ padding: 24 }}>
      <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 2, color: BRAND.muted }}>{title}</div>
      <p style={{ marginTop: 10, color: BRAND.text, fontSize: 17, lineHeight: 1.85 }}>{body}</p>
    </Glass>
  );
}

function ProductIllustration({ productKey }) {
  const { a, b } = PRODUCT_META[productKey];
  return (
    <Glass className="se-hover-up" style={{ position: "relative", overflow: "hidden", minHeight: 420, padding: 28 }}>
      <Glow className="se-pulse" left={-20} top={-10} size={180} from={a} to={b} />
      <Glow className="se-pulse" right={-20} bottom={-20} size={220} from={b} to={a} />
      <GridOverlay />
      <div style={{ position: "relative", zIndex: 2, height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
        <ProductLogoMark product={productKey} size={84} />
        <svg viewBox="0 0 620 340" style={{ width: "100%" }} className="se-float">
          <defs>
            <linearGradient id={`${productKey}-viz`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={a} />
              <stop offset="100%" stopColor={b} />
            </linearGradient>
          </defs>
          <path d="M32 224 C118 84, 212 82, 292 166 S458 288, 580 96" fill="none" stroke={`url(#${productKey}-viz)`} strokeWidth="18" strokeLinecap="round" />
          <path d="M58 264 C150 128, 234 130, 308 208 S460 306, 562 152" fill="none" stroke={`url(#${productKey}-viz)`} strokeWidth="8" strokeLinecap="round" opacity="0.45" />
          <circle cx="32" cy="224" r="13" fill={a} />
          <circle cx="224" cy="100" r="11" fill={b} />
          <circle cx="580" cy="96" r="14" fill={a} />
          <circle cx="494" cy="248" r="10" fill={b} />
          <rect x="396" y="36" rx="20" ry="20" width="168" height="72" fill="rgba(255,255,255,0.78)" stroke="rgba(15,23,42,0.08)" />
          <rect x="82" y="58" rx="20" ry="20" width="130" height="66" fill="rgba(255,255,255,0.76)" stroke="rgba(15,23,42,0.08)" />
        </svg>
      </div>
    </Glass>
  );
}

function PrintedCard({ title, body, colorA, colorB }) {
  return (
    <Glass className="se-hover-up" style={{ padding: 24, position: "relative", overflow: "hidden" }}>
      <Glow className="se-pulse" left={-30} top={-20} size={140} from={colorA} to={colorB} />
      <div style={{ position: "relative", zIndex: 2 }}>
        <div style={{ width: 62, height: 62, borderRadius: 18, background: `linear-gradient(135deg, ${colorA}, ${colorB})`, boxShadow: BRAND.shadowSoft }} />
        <div style={{ marginTop: 18, fontSize: 24, fontWeight: 700, color: BRAND.text }}>{title}</div>
        <p style={{ marginTop: 10, color: BRAND.muted, lineHeight: 1.8 }}>{body}</p>
      </div>
    </Glass>
  );
}

function StakeholderCard({ title, body, emoji }) {
  return (
    <Glass className="se-hover-up" style={{ padding: 24 }}>
      <div style={{ fontSize: 28 }}>{emoji}</div>
      <div style={{ marginTop: 12, fontSize: 22, fontWeight: 700, color: BRAND.text }}>{title}</div>
      <p style={{ marginTop: 10, color: BRAND.muted, lineHeight: 1.8 }}>{body}</p>
    </Glass>
  );
}

function DemoForm({ data }) {
  const [submitted, setSubmitted] = useState(false);
  const [interest, setInterest] = useState(data.contact.interestOptions[0]);
  return (
    <Glass style={{ overflow: "hidden" }}>
      <div style={{ display: "grid", gridTemplateColumns: "0.96fr 1.04fr" }}>
        <div style={{ padding: 32, position: "relative", background: `linear-gradient(135deg, rgba(124,58,237,0.16), rgba(6,182,212,0.10), rgba(249,115,22,0.12))` }}>
          <GridOverlay />
          <div style={{ position: "relative", zIndex: 2 }}>
            <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 2, color: BRAND.muted }}>{data.contact.kicker}</div>
            <h3 style={{ marginTop: 10, fontSize: 40, lineHeight: 1.1, color: BRAND.text }}>{data.contact.title}</h3>
            <p style={{ marginTop: 16, color: BRAND.muted, fontSize: 18, lineHeight: 1.8 }}>{data.contact.body}</p>
            <div style={{ marginTop: 26, display: "grid", gap: 12 }}>
              {[
                "Pilot planning",
                "School demos",
                "Implementation strategy",
                "Partnership conversations",
              ].map((item) => (
                <div key={item} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderRadius: 18, background: "rgba(255,255,255,0.70)", border: `1px solid ${BRAND.line}` }}>
                  <span style={{ color: BRAND.green, fontWeight: 700 }}>✓</span>
                  <span style={{ color: BRAND.text }}>{item}</span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 20, display: "flex", gap: 12, flexWrap: "wrap" }}>
              <a href={WHATSAPP_LINK} className="se-link" style={{ padding: "14px 18px", borderRadius: 999, background: "#25D366", color: "white", fontWeight: 600 }}>WhatsApp</a>
              <a href="mailto:info@datanex.ai" className="se-link" style={{ padding: "14px 18px", borderRadius: 999, background: BRAND.text, color: "white", fontWeight: 600 }}>Email</a>
            </div>
          </div>
        </div>
        <div style={{ padding: 32 }}>
          <form
            style={{ display: "grid", gap: 14, gridTemplateColumns: "1fr 1fr" }}
            onSubmit={(e) => {
              e.preventDefault();
              setSubmitted(true);
              const formData = new FormData(e.currentTarget);
              const payload = Object.fromEntries(formData.entries());
              payload.interest = interest;
              if (typeof window !== "undefined") {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({ event: "demo_submit", interest });
                if (window.fetch) {
                  window.fetch("/api/demo-request", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                  }).catch(() => null);
                }
              }
              e.currentTarget.reset();
            }}
          >
            <Field name="institution" required placeholder={data.contact.fields.institution} />
            <Field name="name" required placeholder={data.contact.fields.name} />
            <Field name="email" type="email" required placeholder={data.contact.fields.email} />
            <Field name="phone" placeholder={data.contact.fields.phone} />
            <SelectField name="role" options={data.contact.roleOptions} />
            <Field name="country" placeholder={data.contact.fields.country} />
            <div style={{ gridColumn: "1 / -1" }}><SelectField value={interest} onChange={(e) => setInterest(e.target.value)} options={data.contact.interestOptions} /></div>
            <div style={{ gridColumn: "1 / -1" }}><Field as="textarea" rows="5" name="message" placeholder={data.contact.fields.message} /></div>
            <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14 }}>
              <div style={{ color: BRAND.muted, fontSize: 14 }}>Analytics-ready multilingual demo funnel</div>
              <button type="submit" style={{ padding: "14px 20px", borderRadius: 999, border: 0, background: BRAND.text, color: "white", fontWeight: 700, cursor: "pointer" }}>{data.contact.cta}</button>
            </div>
            {submitted ? <div style={{ gridColumn: "1 / -1", padding: "14px 16px", borderRadius: 18, background: "rgba(16,185,129,0.10)", border: "1px solid rgba(16,185,129,0.20)", color: BRAND.green }}>{data.contact.success}</div> : null}
          </form>
        </div>
      </div>
    </Glass>
  );
}

function Field({ as = "input", style = {}, ...props }) {
  const Comp = as;
  return <Comp {...props} style={{ width: "100%", padding: "14px 16px", borderRadius: 18, border: `1px solid ${BRAND.line}`, background: "rgba(255,255,255,0.82)", color: BRAND.text, outline: 0, fontSize: 15, ...style }} />;
}

function SelectField({ options, style = {}, ...props }) {
  return (
    <select {...props} style={{ width: "100%", padding: "14px 16px", borderRadius: 18, border: `1px solid ${BRAND.line}`, background: "rgba(255,255,255,0.82)", color: BRAND.text, outline: 0, fontSize: 15, ...style }}>
      {options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
    </select>
  );
}

function Site() {
  useMotionStyles();
  const [lang, setLang] = useState("en");
  const data = useMemo(() => copy[lang] || copy.en, [lang]);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    document.title = `DatanexAI | ${data.hero.title}`;
  }, [data]);

  const navLinks = [
    { label: data.nav.products, href: `${SITE_URL}#products` },
    { label: data.nav.printed, href: `${SITE_URL}#printed` },
    { label: data.nav.outcomes, href: `${SITE_URL}#outcomes` },
    { label: data.nav.compliance, href: `${SITE_URL}#compliance` },
    { label: data.nav.contact, href: `${SITE_URL}#contact` },
  ];

  return (
    <div style={{ minHeight: "100vh", background: `linear-gradient(180deg, ${BRAND.bg} 0%, ${BRAND.bg2} 100%)`, color: BRAND.text }}>
      <header style={{ position: "sticky", top: 0, zIndex: 50, background: "rgba(244,247,251,0.78)", borderBottom: `1px solid ${BRAND.line}`, backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)" }}>
        <div style={{ maxWidth: 1240, margin: "0 auto", padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20 }}>
          <a href={`${SITE_URL}`} className="se-link"><DatanexLogo /></a>
          <nav style={{ display: "flex", alignItems: "center", gap: 22 }} className="desktop-nav">
            <div className="desktop-only" style={{ display: "flex", alignItems: "center", gap: 22 }}>
              {navLinks.map((link) => <a key={link.label} className="se-link" href={link.href} style={{ color: BRAND.muted, fontWeight: 600 }}>{link.label}</a>)}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 999, border: `1px solid ${BRAND.line}`, background: "rgba(255,255,255,0.78)" }}>
              <span style={{ color: BRAND.muted, fontSize: 14 }}>🌐</span>
              <select value={lang} onChange={(e) => setLang(e.target.value)} style={{ border: 0, outline: 0, background: "transparent", color: BRAND.text, fontWeight: 600 }}>
                <option value="en">English</option>
                <option value="pt">Português</option>
                <option value="es">Español</option>
              </select>
            </div>
            <a href={`${SITE_URL}#demo`} className="se-link desktop-only" style={{ padding: "14px 18px", borderRadius: 999, background: BRAND.text, color: "white", fontWeight: 700 }}>{data.nav.demo}</a>
            <button onClick={() => setMobileOpen(!mobileOpen)} style={{ display: "none" }} className="mobile-toggle">☰</button>
          </nav>
        </div>
        {mobileOpen ? (
          <div style={{ padding: "0 20px 18px", borderTop: `1px solid ${BRAND.line}` }} className="mobile-drawer">
            <div style={{ display: "grid", gap: 12, paddingTop: 16 }}>
              {navLinks.map((link) => <a key={link.label} className="se-link" href={link.href} style={{ color: BRAND.text, fontWeight: 600 }}>{link.label}</a>)}
              <a href={`${SITE_URL}#demo`} className="se-link" style={{ padding: "14px 18px", borderRadius: 999, background: BRAND.text, color: "white", fontWeight: 700, textAlign: "center" }}>{data.nav.demo}</a>
            </div>
          </div>
        ) : null}
      </header>

      <Section style={{ paddingTop: 112, paddingBottom: 72 }}>
        <Glow className="se-pulse" left={-40} top={20} size={220} from={BRAND.purple} to={BRAND.magenta} />
        <Glow className="se-pulse" right={-20} top={0} size={260} from={BRAND.cyan} to={BRAND.teal} />
        <div style={{ display: "grid", gridTemplateColumns: "1.02fr 0.98fr", gap: 32, alignItems: "center" }}>
          <div style={{ position: "relative", zIndex: 2 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 999, background: "rgba(255,255,255,0.78)", border: `1px solid ${BRAND.line}`, color: BRAND.muted, fontSize: 13, fontWeight: 600, letterSpacing: 0.3 }}>
              ✦ {data.hero.eyebrow}
            </div>
            <h1 style={{ marginTop: 24, fontSize: 72, lineHeight: 0.98, letterSpacing: -2.2, color: BRAND.text }}> {data.hero.title}</h1>
            <p style={{ marginTop: 18, fontSize: 22, lineHeight: 1.5, color: BRAND.muted, maxWidth: 700 }}>{data.hero.subtitle}</p>
            <p style={{ marginTop: 18, fontSize: 18, lineHeight: 1.85, color: BRAND.muted, maxWidth: 760 }}>{data.hero.body}</p>
            <div style={{ marginTop: 28, display: "flex", gap: 14, flexWrap: "wrap" }}>
              <a href={`${SITE_URL}#demo`} className="se-link" style={{ padding: "16px 22px", borderRadius: 999, background: BRAND.text, color: "white", fontWeight: 700 }}>{data.hero.primary}</a>
              <a href={`${SITE_URL}#products`} className="se-link" style={{ padding: "16px 22px", borderRadius: 999, background: "rgba(255,255,255,0.78)", border: `1px solid ${BRAND.line}`, color: BRAND.text, fontWeight: 700 }}>{data.hero.secondary}</a>
            </div>
            <div style={{ marginTop: 24, display: "flex", gap: 10, flexWrap: "wrap" }}>
              {data.hero.tags.map((tag) => (
                <div key={tag} style={{ padding: "10px 14px", borderRadius: 999, background: "rgba(255,255,255,0.74)", border: `1px solid ${BRAND.line}`, color: BRAND.muted, fontSize: 14, fontWeight: 600 }}>{tag}</div>
              ))}
            </div>
          </div>
          <HeroVisual />
        </div>
      </Section>

      <Section>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 0.96fr", gap: 28, alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 2, color: BRAND.muted }}>{data.problem.kicker}</div>
            <h2 style={{ marginTop: 12, fontSize: 48, lineHeight: 1.05, color: BRAND.text }}>{data.problem.title}</h2>
            <div style={{ marginTop: 20, display: "grid", gap: 14 }}>
              {data.problem.bullets.map((item) => (
                <div key={item} style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "14px 0" }}>
                  <div style={{ width: 28, height: 28, borderRadius: 999, background: "rgba(255,255,255,0.82)", border: `1px solid ${BRAND.line}`, display: "flex", alignItems: "center", justifyContent: "center", color: BRAND.green, fontWeight: 800 }}>✓</div>
                  <div style={{ color: BRAND.text, fontSize: 18, lineHeight: 1.7 }}>{item}</div>
                </div>
              ))}
            </div>
          </div>
          <EcosystemDiagram />
        </div>
      </Section>

      <Section id="products">
        <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 2, color: BRAND.muted }}>{data.productsIntro.kicker}</div>
        <h2 style={{ marginTop: 12, fontSize: 48, lineHeight: 1.05, color: BRAND.text }}>{data.productsIntro.title}</h2>
        <p style={{ marginTop: 16, fontSize: 18, lineHeight: 1.85, color: BRAND.muted, maxWidth: 900 }}>{data.productsIntro.body}</p>
        <div style={{ marginTop: 30, display: "grid", gap: 18 }}>
          {PRODUCTS.map((productKey) => (
            <ProductFeatureCard key={productKey} productKey={productKey} content={data.products[productKey]} labels={{ problem: copy.en.productSections?.problem || data.productsIntro.kicker, unique: copy.en.productSections?.unique || "Why this is different" }} />
          ))}
        </div>
      </Section>

      {PRODUCTS.map((productKey) => (
        <ProductDeepSection key={productKey} productKey={productKey} content={data.products[productKey]} labels={{
          problem: copy[lang].productSections?.problem || (lang === "pt" ? "Problema" : lang === "es" ? "Problema" : "Problem"),
          current: copy[lang].productSections?.current || (lang === "pt" ? "Por que as soluções atuais falham" : lang === "es" ? "Por qué las soluciones actuales fallan" : "Why current solutions fall short"),
          unique: copy[lang].productSections?.unique || (lang === "pt" ? "Por que esta solução é diferente" : lang === "es" ? "Por qué esta solución es diferente" : "Why this solution is different"),
          benefits: copy[lang].productSections?.benefits || (lang === "pt" ? "Benefícios para a escola" : lang === "es" ? "Beneficios para la escuela" : "Benefits to the school"),
          impact: copy[lang].productSections?.impact || (lang === "pt" ? "Impacto em estudantes, pais, professores e administradores" : lang === "es" ? "Impacto en estudiantes, padres, docentes y administradores" : "Impact on students, parents, teachers, and administrators"),
        }} />
      ))}

      <Section id="printed">
        <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 2, color: BRAND.muted }}>{data.printed.kicker}</div>
        <h2 style={{ marginTop: 12, fontSize: 48, lineHeight: 1.05, color: BRAND.text }}>{data.printed.title}</h2>
        <p style={{ marginTop: 16, fontSize: 18, lineHeight: 1.85, color: BRAND.muted, maxWidth: 920 }}>{data.printed.body}</p>
        <div style={{ marginTop: 28, display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 20 }}>
          <PrintedCard title={data.printed.manual.title} body={data.printed.manual.body} colorA={BRAND.blue} colorB={BRAND.cyan} />
          <PrintedCard title={data.printed.journals.title} body={data.printed.journals.body} colorA={BRAND.purple} colorB={BRAND.magenta} />
          <PrintedCard title={data.printed.strategies.title} body={data.printed.strategies.body} colorA={BRAND.orange} colorB={BRAND.gold} />
        </div>
      </Section>

      <Section id="outcomes">
        <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 2, color: BRAND.muted }}>{data.outcomes.kicker}</div>
        <h2 style={{ marginTop: 12, fontSize: 48, lineHeight: 1.05, color: BRAND.text }}>{data.outcomes.title}</h2>
        <div style={{ marginTop: 28, display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 20 }}>
          <StakeholderCard title="Students" body={data.outcomes.students} emoji="🎓" />
          <StakeholderCard title="Parents" body={data.outcomes.parents} emoji="👨‍👩‍👧" />
          <StakeholderCard title="Teachers" body={data.outcomes.teachers} emoji="📘" />
          <StakeholderCard title="Administrators" body={data.outcomes.admins} emoji="🏫" />
        </div>
      </Section>

      <Section id="compliance">
        <Glass className="se-hover-up" style={{ position: "relative", overflow: "hidden", padding: 34 }}>
          <Glow className="se-pulse" right={-20} top={-20} size={180} from={BRAND.purple} to={BRAND.cyan} />
          <div style={{ position: "relative", zIndex: 2, display: "grid", gridTemplateColumns: "0.92fr 1.08fr", gap: 28, alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 2, color: BRAND.muted }}>{data.compliance.kicker}</div>
              <h2 style={{ marginTop: 12, fontSize: 42, lineHeight: 1.08, color: BRAND.text }}>{data.compliance.title}</h2>
            </div>
            <p style={{ margin: 0, fontSize: 18, lineHeight: 1.85, color: BRAND.muted }}>{data.compliance.body}</p>
          </div>
        </Glass>
      </Section>

      <Section id="demo">
        <DemoForm data={data} />
      </Section>

      <Section id="contact" style={{ paddingTop: 0 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 28, alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 2, color: BRAND.muted }}>{data.contact.kicker}</div>
            <h2 style={{ marginTop: 12, fontSize: 48, lineHeight: 1.05, color: BRAND.text }}>{data.contact.title}</h2>
            <p style={{ marginTop: 16, fontSize: 18, lineHeight: 1.85, color: BRAND.muted, maxWidth: 920 }}>{data.contact.body}</p>
          </div>
          <Glass className="se-hover-up" style={{ padding: 24 }}>
            <div style={{ display: "grid", gap: 14, color: BRAND.text, minWidth: 300 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>✉️ info@datanex.ai</div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>📞 +55 (11) 0000-0000</div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>🌐 {SITE_URL}</div>
            </div>
          </Glass>
        </div>
      </Section>

      <a href={WHATSAPP_LINK} className="se-link" style={{ position: "fixed", right: 20, bottom: 20, zIndex: 60, padding: "15px 18px", borderRadius: 999, background: "#25D366", color: "white", fontWeight: 800, boxShadow: "0 18px 40px rgba(37,211,102,0.28)" }}>
        WhatsApp
      </a>

      <footer style={{ borderTop: `1px solid ${BRAND.line}`, marginTop: 40, background: "rgba(255,255,255,0.55)" }}>
        <div style={{ maxWidth: 1240, margin: "0 auto", padding: "24px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20, flexWrap: "wrap" }}>
          <div style={{ fontWeight: 700, color: BRAND.text }}>DatanexAI</div>
          <div style={{ color: BRAND.muted }}>© {new Date().getFullYear()} DatanexAI. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}

export default function App() {
  return <Site />;
}
